/**
 * @file index.tsx
 *
 * @fileoverview Shows a public organization profile sidebar.
 * Handles different view representation depending on user authorities
 * and current organization profile data.
 */
import { OrganizationBenefitsWidget } from '~/jobiqo/components/Organization/Widgets/Benefits';
import { OrganizationContactsWidget } from '~/jobiqo/components/Organization/Widgets/Contacts';
import { OrganizationAttachmentsWidget } from '~/jobiqo/components/Organization/Widgets/Attachments';
import {
  OrganizationSocialLinksWidget,
  organizationHasSocialLinks,
} from '~/jobiqo/components/Organization/Widgets/SocialLinks';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { OneSidebarLayout } from '~/vcl/templates/01-layout/02-grid-layouts/02-one-sidebar';

export interface OrganizationDetailSidebarProps {
  /**
   * The current organization.
   */
  organization: OrganizationProfile;
  /**
   * Children elements.
   */
  children: React.ReactNode;
}

export const OrganizationDetailSidebar = ({
  organization,
  children,
}: OrganizationDetailSidebarProps) => {
  const { canEdit, contactPersons, benefits, attachments } = organization;
  const hasSocialLinks = organizationHasSocialLinks(organization);

  let SidebarLayout: React.ReactNode | null = (
    <>
      <OrganizationSocialLinksWidget organization={organization} />
      <OrganizationContactsWidget organization={organization} boxId="contact" />
      <OrganizationBenefitsWidget organization={organization} />
      <OrganizationAttachmentsWidget organization={organization} />
    </>
  );

  // Return null so that the sidebar does not take space
  if (
    !canEdit &&
    !hasSocialLinks &&
    !contactPersons?.length &&
    !benefits?.length &&
    !attachments?.length
  ) {
    SidebarLayout = null;
  }

  return (
    <OneSidebarLayout sidebarPosition="right" sidebar={SidebarLayout}>
      {children}
    </OneSidebarLayout>
  );
};
